import { Injectable } from '@angular/core';
import type { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { BillingPlanTier } from '@core/products-types';
import type { IconName } from '@core/user-interface-legacy';

import { ready } from '../../../helpers/rx-operators';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../../types/pricing-feature.types';
import { ProductOrderStepUrl } from '../product-order.types';
import type {
  ContinueVariant,
  MigrationDataUI,
  TopCardColor,
} from './product-order-migration-ui.types';
import { FeatureDefinitions as Defs } from './product-order-migration.data';

enum MigrationMode {
  LaunchStart,
  LaunchGrow,
  ScaleStart,
  ScaleGrow,
}

const features = {
  [MigrationMode.LaunchStart]: {
    topCardTopSection: [
      Defs.pipelineTemplates,
      Defs.pipelineAutomations,
      Defs.automatedEmails,
      Defs.webhooks,
      Defs.profilePrint,
      Defs.roleBasedAccess,
      Defs.visibility,
      Defs.eeo,
    ],
    bottomCard: [Defs.fairEvaluations],
  },
  [MigrationMode.LaunchGrow]: {
    topCardTopSection: [Defs.unlimitedJobs],
    topCardBottomSection: [
      [
        Defs.pipelineTemplates,
        Defs.pipelineAutomations,
        Defs.automatedEmails,
        Defs.webhooks,
        Defs.profilePrint,
        Defs.roleBasedAccess,
        Defs.visibility,
        Defs.eeo,
      ],
      [
        Defs.careersHub,
        Defs.referrals,
        Defs.eventScheduler,
        Defs.questionnaires,
        Defs.knockoutQuestions,
        Defs.multiLocations,
        Defs.multiLanguages,
        Defs.customReporting,
      ],
    ],
  },
  [MigrationMode.ScaleStart]: {
    topCardTopSection: [
      [
        Defs.pipelineTemplates,
        Defs.pipelineAutomations,
        Defs.automatedEmails,
        Defs.customReporting,
        Defs.eventScheduler,
      ],
      [
        Defs.knockoutQuestions,
        Defs.questionnaires,
        Defs.multiLocations,
        Defs.multiLanguages,
        Defs.careersHub,
      ],
    ],
  },
  [MigrationMode.ScaleGrow]: {
    topCardTopSection: [Defs.unlimitedJobs],
    topCardBottomSection: [Defs.referrals],
  },
};

@Injectable()
export class ProductOrderMigrationLogicService {
  private readonly model: Record<MigrationMode, MigrationDataUI>;
  private readonly migrationModeMap = {
    [BillingPlanTier.Launch]: {
      [BillingPlanTier.Start]: MigrationMode.LaunchStart,
      [BillingPlanTier.Grow]: MigrationMode.LaunchGrow,
    },
    [BillingPlanTier.Scale]: {
      [BillingPlanTier.Start]: MigrationMode.ScaleStart,
      [BillingPlanTier.Grow]: MigrationMode.ScaleGrow,
    },
  };

  constructor(
    private pricingFacade: PricingFeatureFacade,
    private pricingModel: PricingFeatureObservableModel,
    private router: Router,
    private i18n: I18nService,
  ) {
    this.model = this.buildModel();
  }

  public getData$(selectedProductId: number): Observable<MigrationDataUI> {
    return combineLatest({
      activeTier: this.pricingModel.pipe(
        ready,
        map(model => model.subscription?.tier),
        filterBoolean,
        map(tier => <BillingPlanTier.Launch | BillingPlanTier.Scale>tier),
      ),
      selectedTier: this.pricingFacade.getBillingPlans().pipe(
        map(plans => plans.find(plan => plan.id === selectedProductId)),
        filterBoolean,
        map(plan => <BillingPlanTier.Start | BillingPlanTier.Grow>plan.tier),
      ),
    }).pipe(
      map(({ activeTier, selectedTier }) => this.migrationModeMap[activeTier][selectedTier]),
      map(mode => ({ ...this.model[mode] })),
    );
  }

  public continue(route: ActivatedRoute) {
    this.router.navigate(['../', ProductOrderStepUrl.OrderBilling], {
      relativeTo: route,
      state: {
        migrationScreenViewed: true,
      },
    });
  }

  public back() {
    this.pricingFacade.openPlanPicker();
  }

  // eslint-disable-next-line max-lines-per-function
  private buildModel() {
    const startPlanName = this.t('app.pricing.start');
    const growPlanName = this.t('app.pricing.grow');
    return {
      [MigrationMode.LaunchStart]: {
        heading: this.t('app.pricing.plan_picker_2024.migration.upgrade_to', {
          planName: startPlanName,
        }),
        topCard: {
          heading: this.t('app.pricing.plan_picker_2024.migration.new_features_with', {
            planName: startPlanName,
          }),
          subheading: this.t('app.pricing.plan_picker_2024.migration.unlock_x_features', {
            count: 8,
          }),
          featuresTop: [
            features[MigrationMode.LaunchStart].topCardTopSection.map(item =>
              this.mapFeature(item, 'add-circle-outline'),
            ),
          ],
          color: <TopCardColor>'start',
        },
        bottomCard: {
          heading: this.t('app.pricing.plan_picker_2024.migration.features_lose'),
          features: features[MigrationMode.LaunchStart].bottomCard.map(item =>
            this.mapFeature(item, 'remove-circle'),
          ),
          infobox: this.t('app.pricing.plan_picker_2024.migration.need_flexibility'),
        },
        continueLabel: this.t('app.common.continue'),
        continueVariant: <ContinueVariant>'primary',
      },
      [MigrationMode.LaunchGrow]: {
        heading: this.t('app.pricing.plan_picker_2024.migration.upgrade_to', {
          planName: growPlanName,
        }),
        topCard: {
          heading: this.t('app.pricing.plan_picker_2024.migration.new_features_with', {
            planName: growPlanName,
          }),
          subheading: this.t('app.pricing.plan_picker_2024.migration.unlock_x_features', {
            count: 17,
          }),
          featuresTop: [
            features[MigrationMode.LaunchGrow].topCardTopSection.map(item =>
              this.mapFeature(item, 'add-circle-outline', false),
            ),
          ],
          featuresBottom: features[MigrationMode.LaunchGrow].topCardBottomSection.map(group =>
            group.map(item => this.mapFeature(item, 'add-circle-outline')),
          ),
          color: <TopCardColor>'grow',
        },
        continueLabel: this.t('app.common.continue'),
        continueVariant: <ContinueVariant>'primary',
      },
      [MigrationMode.ScaleStart]: {
        heading: this.t('app.pricing.plan_picker_2024.migration.downgrade_to', {
          planName: startPlanName,
        }),
        topCard: {
          color: <TopCardColor>'danger',
          heading: this.t('app.pricing.plan_picker_2024.migration.features_lose_after_downgrade'),
          featuresTop: features[MigrationMode.ScaleStart].topCardTopSection.map(column =>
            column.map(item => this.mapFeature(item, 'remove-circle', false)),
          ),
          infobox: this.t('app.pricing.plan_picker_2024.migration.need_flexibility'),
          // ytVideoId: this.t(ScaleToStartVideoId), Bring back when video is ready
        },
        continueLabel: this.t('app.common.continue'),
        continueVariant: <ContinueVariant>'danger',
      },
      [MigrationMode.ScaleGrow]: {
        heading: this.t('app.pricing.plan_picker_2024.migration.upgrade_to', {
          planName: growPlanName,
        }),
        topCard: {
          heading: this.t('app.pricing.plan_picker_2024.migration.new_features_with', {
            planName: growPlanName,
          }),
          subheading: this.t('app.pricing.plan_picker_2024.migration.unlock_x_features', {
            count: 2,
          }),
          featuresTop: [
            features[MigrationMode.ScaleGrow].topCardTopSection.map(item =>
              this.mapFeature(item, 'add-circle-outline', false),
            ),
          ],
          featuresBottom: [
            features[MigrationMode.ScaleGrow].topCardBottomSection.map(item =>
              this.mapFeature(item, 'add-circle-outline'),
            ),
          ],
          color: <TopCardColor>'grow',
        },
        continueLabel: this.t('app.common.continue'),
        continueVariant: <ContinueVariant>'primary',
      },
    };
  }

  private mapFeature = (
    feature: { label: string; tooltip?: string },
    icon: IconName,
    isBold = true,
  ) => ({
    label: this.t(feature.label),
    altIcon: icon,
    isBold,
    ...(feature.tooltip && { tooltip: this.t(feature.tooltip) }),
  });

  private t = (key: string, options?: any) => this.i18n.translate(key, options);
}
